import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BagHappy } from "iconsax-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { Link } from "@/components/Atoms";
import { SefanoButton } from "@/components/Molecules";
import { OrdersTable } from "@/components/Organisms/OrdersTable";
import type { LatestOrdersCardProps } from "./LatestOrdersCard.types";
import * as styles from "./LatestOrdersCard.styles";

export type { LatestOrdersCardProps } from "./LatestOrdersCard.types";

export const LatestOrdersCard = memo(function LatestOrdersCard({
  orders,
  isLoading = false,
  isError = false,
  onCreateFirstClick,
}: LatestOrdersCardProps) {
  const { t } = useTranslation("home");
  const theme = useTheme();

  return (
    <Box sx={styles.card}>
      <Box sx={styles.header}>
        <Typography variant="h6" sx={styles.title}>
          {t("dashboard.orders.title")}
        </Typography>
        {orders.length > 0 ? (
          <Link to="/orders">
            <Typography variant="body2" color="primary" sx={styles.viewAllLink}>
              {t("dashboard.orders.viewAll")}
            </Typography>
          </Link>
        ) : null}
      </Box>

      {isLoading ? (
        <Box sx={styles.loadingState}>
          <CircularProgress size={32} />
        </Box>
      ) : isError ? (
        <Typography variant="body2" sx={styles.errorState}>
          {t("dashboard.orders.error")}
        </Typography>
      ) : orders.length === 0 ? (
        <Box sx={styles.emptyBox}>
          <Box sx={styles.emptyIconWrap(theme)}>
            <BagHappy size={60} variant="Bold" color={theme.palette.primary.main} />
          </Box>
          <Typography sx={styles.emptyTitle}>{t("dashboard.orders.emptyTitle")}</Typography>
          <Typography sx={styles.emptyDescription}>{t("dashboard.orders.emptyDescription")}</Typography>
          <Box sx={styles.emptyActionWrap}>
            <SefanoButton intent="add" onClick={onCreateFirstClick}>
              {t("dashboard.orders.createFirst")}
            </SefanoButton>
          </Box>
        </Box>
      ) : (
        <Box sx={styles.tableWrap}>
          <OrdersTable orders={orders} />
        </Box>
      )}
    </Box>
  );
});
